import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';

const CONNECTION_ERRORS = [AxiosError.ERR_NETWORK, AxiosError.ECONNABORTED, AxiosError.ETIMEDOUT];

interface FastAPIError {
  detail: string;
}

/**
 * Renders a toast message displaying the error.
 * Handles the logging and reporting of errors to Sentry based on the error type.
 * Differentiates handling based on custom messages, connection errors, FastAPI specific responses,
 * and other unexpected JavaScript errors.
 *
 * @param error The error object caught in a try-catch block, expected to be of unknown type that can be an AxiosError.
 * @param customMessage An optional custom error message to be displayed to the user via toast.
 */
const errorHandler = (error: unknown, customMessage?: string): void => {
  // Ensure the error is of type AxiosError for better TypeScript support
  const fastError = error as AxiosError<FastAPIError>;

  // Log the error
  console.error(error);

  // Customize what is sent to Sentry based on the error type
  if (fastError.response) {
    // Construct a detailed error object for Sentry
    const errorDetails = {
      message: fastError.message,
      name: fastError.name,
      code: fastError.code,
      status: fastError.response.status,
      detail: fastError.response.data.detail,
    };

    // Send the detailed error object to Sentry
    Sentry.captureException(new Error(fastError.response.data.detail), { extra: errorDetails });
  } else {
    // Send the entire error if not a FastAPI response error
    Sentry.captureException(error);
  }

  // Custom UI message handling
  let errorMessage = '';

  if (customMessage) {
    errorMessage = customMessage;
  } else if (fastError.code && CONNECTION_ERRORS.includes(fastError.code)) {
    // Handle specific network errors uniquely
    errorMessage = 'Please check your internet connection and try again';
  } else if (fastError.response?.data.detail) {
    // Display errors from FastAPI response
    errorMessage = fastError.response?.data.detail;
  } else if (error instanceof Error) {
    // Fallback for standard JavaScript errors
    errorMessage = error.message;
  } else {
    // Generic error message as a last resort
    errorMessage = 'An unexpected error occurred';
  }

  toast.error(
    errorMessage,
    // we set a toast id because we only want one error message per type shown
    { toastId: errorMessage },
  );
};

export default errorHandler;
